/**
 * voice-v8 — Fantasiewörter aus dem Rauschen am Ende der Mitschrift. (03.10.2026)
 *
 * Martin, 03.10. im Auto: Jev gibt das Mikro zu spät frei. Gemessen: fertig
 * 0,07–0,14 bei 1,0–1,7 s Stille, entscheid=warten. Verdacht: die Erkennung
 * hängt im Autobrummen Wörter an, die niemand sagte — Jev sieht dann einen
 * offenen Satz („Wie wird das Wetter morgen? Ähm so“) und wartet.
 *
 * Die App erkennt solche Endwörter am Pegel (frontend/lib/rausch-woerter.ts:
 * das Wort begann in der laufenden Stille, und der Pegel blieb für seine
 * ganze Dauer unter der Stille-Grenze) und schickt ihre ANZAHL mit der Frage.
 * Hier steht EINMAL, wie sie für das Urteil wegfallen — App und Server
 * rechnen damit, damit das Urteil zum Text der App passt.
 *
 * ⚠️ Nur für die ENTSCHEIDUNG. Der Text, der hinausgeht, bleibt wie erkannt.
 * ⚠️ Nie alles: bliebe nichts übrig, fällt nichts weg (altes Verhalten).
 */

/** Höchstens so viele Endwörter werden betrachtet (und gemessen). */
export const RAUSCH_HOECHSTENS = 6;

/** Der Text ohne seine letzten `k` Wörter — oder unverändert, wenn nichts bliebe. */
export function ohneEndWoerter(text: string, k: number): string {
  const t = text.trim();
  const n = Math.min(RAUSCH_HOECHSTENS, Math.max(0, Math.floor(k)));
  if (n === 0) return t;
  const woerter = t.split(/\s+/u).filter(Boolean);
  if (n >= woerter.length) return t;
  return woerter.slice(0, woerter.length - n).join(" ");
}

/**
 * Die Messzeile zu den letzten Wörtern — NUR Zahlen und Kennzeichen, nie der Wortlaut (Datenschutz, 18.10.2026:
 * das Journal trägt keinen Gesprächsinhalt). `endwoerter` = Anzahl der betrachteten Endwörter (höchstens n),
 * `letztlen` = Länge des letzten Wortes ohne Satzzeichen, `endzeichen` = letztes Zeichen (? . ! , oder keins),
 * `endwort` = zahl, wenn das letzte Wort eine Zahl ist, sonst sonst. NUR für die Zeile `[stille-auto]` (Konto 1).
 */
export function endWortMass(text: string, n = RAUSCH_HOECHSTENS): string {
  const woerter = text
    .replace(/[\p{Cf}\p{Cc}"]/gu, " ")
    .trim()
    .split(/\s+/u)
    .filter(Boolean);
  const letztes = woerter[woerter.length - 1] ?? "";
  const ohneZeichen = letztes.replace(/[\p{P}\p{S}]+$/u, "");
  const zeichen = /[?.!,]$/u.exec(letztes)?.[0] ?? "keins";
  const art = /^[\d.,]+$/u.test(ohneZeichen) && ohneZeichen !== "" ? "zahl" : "sonst";
  return `endwoerter=${Math.min(n, woerter.length)} letztlen=${[...ohneZeichen].length} endzeichen=${zeichen} endwort=${art}`;
}

/** Was die App zu den Endwörtern gemessen hat — je Wort, in Reihenfolge, höchstens RAUSCH_HOECHSTENS. */
export interface RauschMessung {
  /** So viele Endwörter hält die App für Rauschen (am Ende, ohne Lücke). */
  k: number;
  /** Höchster Pegel (0…1) im Zeitraum des Wortes; `null` ohne Zeitmarken oder Bild. */
  pegel: (number | null)[];
  /** Die Konfidenz der Erkennung (Soniox `confidence`, kleinste der Teilstücke). */
  konf: (number | null)[];
  /** Begann das Wort in der laufenden Stille? `null`: nicht zu sagen. */
  still: (boolean | null)[];
  /** Die Stille-Grenze zuletzt (folgt dem Grundpegel). */
  grenze: number | null;
}

const zahl01 = (v: unknown): number | null =>
  typeof v === "number" && Number.isFinite(v) && v >= 0 && v <= 1 ? Math.round(v * 1000) / 1000 : null;

/** Die Messung aus einer Frage — alles Unplausible fällt weg, `null` ohne brauchbare Angabe. */
export function liesRauschMessung(roh: unknown): RauschMessung | null {
  if (typeof roh !== "object" || roh === null) return null;
  const o = roh as Record<string, unknown>;
  const k = typeof o.k === "number" && Number.isInteger(o.k) && o.k >= 0 && o.k <= RAUSCH_HOECHSTENS ? o.k : null;
  if (k === null) return null;
  const liste = <T>(v: unknown, f: (x: unknown) => T): T[] =>
    Array.isArray(v) ? v.slice(-RAUSCH_HOECHSTENS).map(f) : [];
  return {
    k,
    pegel: liste(o.pegel, zahl01),
    konf: liste(o.konf, zahl01),
    still: liste(o.still, (x) => (typeof x === "boolean" ? x : null)),
    grenze: zahl01(o.grenze),
  };
}
